import React, { useEffect, useState, useMemo } from 'react';
import { MAINTENANCE_ITEMS, getSelectableLocations, getMaintenanceStatus, getBreweryMaintenanceStatus } from '../../kegops-utils';
import * as SupabaseService from '../../supabaseService';

// For tests we may mock fetch functions; keep them here for easy stubbing
import { LocationMaintenanceModal } from '../../modals/kegModals';
import { MassMaintenanceModal } from '../../modals/massMaintenanceModal';

const StatusBadge = ({ status, label }) => {
  const cls = status === 'overdue' ? 'bg-red-100 text-red-800 border-red-200' : status === 'due-soon' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : status === 'never' ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : 'bg-green-100 text-green-800 border-green-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
};

const KegCard = ({ keg, settings, onOpenMaintenance }) => {
  // render maintenance item badges
  return (
    <div className="bg-white dark:bg-gray-800 border rounded p-3 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold">{keg.name} <span className="text-xs text-gray-500">#{keg.kegNumber}</span></div>
          <div className="text-xs text-gray-500">{keg.location}{keg.sublocation ? ` → ${keg.sublocation}` : ''}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onOpenMaintenance(keg)} className="px-2 py-1 bg-amber-600 text-white rounded text-sm">Add</button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {MAINTENANCE_ITEMS.filter(i => i !== 'Line Cleaning' && i !== 'Water Filtration').map(item => {
          const status = getMaintenanceStatus(keg, item, settings);
          return (
            <div key={item} className="flex items-center justify-between gap-2">
              <div className="text-xs text-gray-700 dark:text-gray-300">{item}</div>
              <div>{status.neverDone ? <StatusBadge status='never' label='Never' /> : status.status === 'ok' ? <StatusBadge status='ok' label='OK' /> : status.status === 'due-soon' ? <StatusBadge status='due-soon' label='Due' /> : <StatusBadge status='overdue' label='Overdue' />}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LocationCard = ({ location, sublocation, tapNames, tapCount, tapStatusRow, settings, onRecord }) => {
  const name = sublocation ? `${location} → ${sublocation}` : location;
  const taps = tapNames && tapNames.length > 0 ? tapNames : Array.from({ length: tapCount || 0 }, (_, i) => `Tap ${i + 1}`);

  // tapStatusRow may now be an array of rows per-tap
  const rowsForThis = Array.isArray(tapStatusRow) ? tapStatusRow : (tapStatusRow ? [tapStatusRow] : []);

  // Determine grid columns: up to 8 per row and wrap to second row if more
  const cols = Math.min(8, Math.max(1, Math.ceil((taps.length) / (taps.length > 8 ? 2 : 1))));

  return (
    <div className="bg-white dark:bg-gray-800 border rounded p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold">{name}</div>
        <div className="text-xs text-gray-500">{taps.length} taps</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '0.5rem' }}>
        {taps.map((tap, idx) => {
          // Find the matching per-tap row by tap_name or fallback to a null-tap record or 0
          const matchingRow = rowsForThis.find(r => (r.tap_name || '') === tap) || rowsForThis.find(r => r.tap_name === null) || null;
          const batchCount = matchingRow ? matchingRow.batch_count : 0;
          const status = batchCount >= (settings?.maintenanceIntervals?.['Line Cleaning']?.batches || 10) ? 'overdue' : (batchCount >= Math.max(1, (settings?.maintenanceIntervals?.['Line Cleaning']?.batches || 10) - 1) ? 'due-soon' : 'ok');
          return (
            <div key={idx} className="p-2 border rounded text-xs flex items-center justify-between">
              <div className="truncate">{tap}</div>
              <div>{status === 'ok' ? <StatusBadge status='ok' label={`${batchCount}`} /> : status === 'due-soon' ? <StatusBadge status='due-soon' label={`${batchCount}`} /> : <StatusBadge status='overdue' label={`${batchCount}`} />}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-right">
        <button onClick={() => onRecord({ location, sublocation })} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">Record Line Cleaning</button>
      </div>
    </div>
  );
};

export const DashboardMaintenanceTab = ({ kegs, locations, settings, selectedBrewery, onOpenMaintenance, onNavigateToSettings }) => {
  const [tapLineStatuses, setTapLineStatuses] = useState([]);
  const [typeFilters, setTypeFilters] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationMaintenanceForm, setLocationMaintenanceForm] = useState({ date: new Date().toISOString().split('T')[0], items: ['Line Cleaning'], notes: '', taps: [], tapsAll: false });
  const [selectedLocationName, setSelectedLocationName] = useState('');
  const [showMassModal, setShowMassModal] = useState(false);
  const [massModalInitialCategory, setMassModalInitialCategory] = useState(null);
  const [massModalInitialFilters, setMassModalInitialFilters] = useState([]);
  const [waterRecords, setWaterRecords] = useState([]);
  const [waterBatchCount, setWaterBatchCount] = useState(0);

  // Get water filtration settings from brewery
  const waterFiltration = selectedBrewery?.water_filtration || {};
  const waterFiltrationEnabled = !!waterFiltration.enabled;
  const enabledFilterTypes = waterFiltration.types || [];

  useEffect(() => {
    const load = async () => {
      if (!selectedBrewery?.id) return;
      const [t, lm, batchRes] = await Promise.all([
        SupabaseService.fetchTapLineStatuses(selectedBrewery.id),
        SupabaseService.fetchLocationMaintenanceRecords(selectedBrewery.id, 50),
        SupabaseService.fetchWaterFiltrationBatchCount(selectedBrewery.id)
      ]);
      if (t.success) setTapLineStatuses(t.data || []);
      if (lm.success) setWaterRecords((lm.data || []).filter(r => r.location === 'Water Filtration'));
      if (batchRes.success) setWaterBatchCount(batchRes.data || 0);
    };
    load();
  }, [selectedBrewery]);

  // Keg-focused list for item-specific snapshot
  const itemSnapshot = useMemo(() => {
    if (!selectedItem) return null;
    const list = kegs.map(keg => {
      const status = getMaintenanceStatus(keg, selectedItem, settings);
      return {
        id: keg.id,
        name: keg.name,
        kegNumber: keg.kegNumber,
        location: keg.location,
        sublocation: keg.sublocation,
        status: status.status,
        message: status.message
      };
    });
    const overdue = list.filter(l => l.status === 'overdue');
    const dueSoon = list.filter(l => l.status === 'due-soon');
    const healthy = list.filter(l => l.status === 'ok');
    return { list, overdue, dueSoon, healthy };
  }, [selectedItem, kegs, settings]);

  const selectableLocations = getSelectableLocations(locations);


  const [selectedLocationTaps, setSelectedLocationTaps] = useState([]);

  const handleRecordLocation = (loc) => {
    const name = loc.sublocation ? `${loc.location} - ${loc.sublocation}` : loc.location;
    setSelectedLocationName(name);
    setSelectedLocationTaps(loc.tapNames || []);
    // reset form taps
    setLocationMaintenanceForm({ ...locationMaintenanceForm, taps: [], tapsAll: false });
    setShowLocationModal(true);
  };

  const handleSaveLocationMaintenance = async () => {
    if (!selectedBrewery?.id) return alert('Select a brewery');
    const [loc, sub] = selectedLocationName.includes(' - ') ? selectedLocationName.split(' - ') : [selectedLocationName, null];
    const tapNamesParam = locationMaintenanceForm.tapsAll ? null : (locationMaintenanceForm.taps && locationMaintenanceForm.taps.length > 0 ? locationMaintenanceForm.taps : null);
    const result = await SupabaseService.recordLocationMaintenance(selectedBrewery.id, loc, sub || null, locationMaintenanceForm.items, locationMaintenanceForm.notes, tapNamesParam);
    if (result.success) {
      alert('Recorded maintenance');
      setShowLocationModal(false);
      const t = await SupabaseService.fetchTapLineStatuses(selectedBrewery.id);
      if (t.success) setTapLineStatuses(t.data || []);
    } else {
      alert('Error recording maintenance: ' + result.error);
    }
  };

  // Aggregate tap line status map by location+sublocation for quick lookup
  // Each key maps to an array of tap_status rows (per-tap)
  const tapMap = useMemo(() => {
    const m = {};
    tapLineStatuses.forEach(t => { 
      const key = `${t.location}||${t.sublocation || ''}`;
      if (!m[key]) m[key] = [];
      m[key].push(t);
    });
    return m;
  }, [tapLineStatuses]);

  // UI: if selectedItem is set, show the item snapshot view
  return (
    <div>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded p-4 mb-4 flex flex-col md:flex-row gap-3 items-start md:items-center">

        <div className="flex items-center gap-2 mr-2">
          <button onClick={() => setShowMassModal(true)} className="px-3 py-1 bg-amber-600 text-white rounded text-sm">Add Maintenance Activity</button>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm mr-2 hidden md:block">Types:</div>
            <div className="flex gap-2 flex-wrap">
              {MAINTENANCE_ITEMS.filter(t => t !== 'Water Filtration').map(t => (
                <button key={t} onClick={() => setSelectedItem(selectedItem === t ? '' : t)} className={`px-3 py-1 rounded text-sm ${selectedItem === t ? 'bg-amber-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
                  {t}
                </button>
              ))}
              {/* Category quick toggles */}
              {['O-Ring', 'Gasket', 'Cleaning', 'Tear Down'].map(t => (
                <button key={t} onClick={() => setTypeFilters(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])} className={`px-3 py-1 rounded text-sm ${typeFilters.includes(t) ? 'bg-amber-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 md:mt-0">
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} className="border rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full md:w-48">
              <option value="">All locations</option>
              {selectableLocations.map(opt => (
                <option key={opt.displayName} value={opt.displayName}>{opt.displayName}</option>
              ))}
            </select>
          </div>
        </div>




      </div>

      {selectedItem ? (
        // Item snapshot view
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{selectedItem} — Overview</h3>
              <p className="text-sm text-gray-500">Quick snapshot of keg health for {selectedItem}</p>
            </div>
            <div className="flex gap-3 items-center">
              <div className="text-sm">Overdue: <span className="font-semibold text-red-600">{itemSnapshot.overdue.length}</span></div>
              <div className="text-sm">Due Soon: <span className="font-semibold text-yellow-600">{itemSnapshot.dueSoon.length}</span></div>
              <div className="text-sm">Healthy: <span className="font-semibold text-green-600">{itemSnapshot.healthy.length}</span></div>
            </div>
          </div>

          <div className="grid gap-3">
            {itemSnapshot.overdue.map(k => (
              <div key={`od-${k.id}`} className="bg-red-50 border border-red-200 rounded p-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{k.name} <span className="text-xs text-gray-500">#{k.kegNumber}</span></div>
                  <div className="text-xs text-gray-500">{k.location}{k.sublocation ? ` → ${k.sublocation}` : ''}</div>
                </div>
                <div className="text-sm text-red-700">Overdue</div>
              </div>
            ))}

            {itemSnapshot.dueSoon.map(k => (
              <div key={`ds-${k.id}`} className="bg-yellow-50 border border-yellow-200 rounded p-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{k.name} <span className="text-xs text-gray-500">#{k.kegNumber}</span></div>
                  <div className="text-xs text-gray-500">{k.location}{k.sublocation ? ` → ${k.sublocation}` : ''}</div>
                </div>
                <div className="text-sm text-yellow-800">Due Soon</div>
              </div>
            ))}

            {itemSnapshot.healthy.map(k => (
              <div key={`ok-${k.id}`} className="bg-green-50 border border-green-200 rounded p-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{k.name} <span className="text-xs text-gray-500">#{k.kegNumber}</span></div>
                  <div className="text-xs text-gray-500">{k.location}{k.sublocation ? ` → ${k.sublocation}` : ''}</div>
                </div>
                <div className="text-sm text-green-700">Healthy</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // General view: Keg tiles and Location grid
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {showMassModal && selectedBrewery && (
            <MassMaintenanceModal
              show={showMassModal}
              onClose={() => setShowMassModal(false)}
              breweryId={selectedBrewery.id}
              kegs={kegs}
              locations={locations}
              initialCategory={massModalInitialCategory}
              initialFilterTypes={massModalInitialFilters}
              enabledFilterTypes={enabledFilterTypes}
              onSaved={async () => { 
                const t = await SupabaseService.fetchTapLineStatuses(selectedBrewery.id); 
                if (t.success) setTapLineStatuses(t.data || []); 
                const lm = await SupabaseService.fetchLocationMaintenanceRecords(selectedBrewery.id, 50); 
                if (lm.success) setWaterRecords((lm.data || []).filter(r => r.location === 'Water Filtration')); 
                const batchRes = await SupabaseService.fetchWaterFiltrationBatchCount(selectedBrewery.id);
                if (batchRes.success) setWaterBatchCount(batchRes.data || 0);
              }}
            />
          )}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold mb-3">Kegs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {kegs.filter(k => {
                if (locationFilter) return `${k.location}${k.sublocation ? ` → ${k.sublocation}` : ''}` === locationFilter;
                return true;
              }).map(keg => (
                <KegCard key={keg.id} keg={keg} settings={settings} onOpenMaintenance={onOpenMaintenance} />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Beer Lines and Water Filtration</h3>
            </div>

            <div className="space-y-3">
              {locations.map(loc => (
                loc.sublocations && loc.sublocations.length > 0 ? loc.sublocations.map(sub => (
                  <LocationCard key={`${loc.name}-${sub.name}`} location={loc.name} sublocation={sub.name} tapNames={sub.tapNames} tapCount={sub.tapCount} tapStatusRow={tapMap[`${loc.name}||${sub.name}`]} settings={settings} onRecord={() => handleRecordLocation({ location: loc.name, sublocation: sub.name })} />
                )) : (
                  <LocationCard key={loc.name} location={loc.name} sublocation={''} tapNames={loc.tapNames} tapCount={loc.tapCount} tapStatusRow={tapMap[`${loc.name}||`]} settings={settings} onRecord={() => handleRecordLocation({ location: loc.name, sublocation: '' })} />
                )
              ))}
            </div>

            {/* Water Filtration (account-level maintenance) */}
            <div className="mt-6 p-4 border rounded bg-white dark:bg-gray-800">{!waterFiltrationEnabled ? (
                <div className="text-center py-6">
                  <div className="text-gray-400 mb-3">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  </div>
                  <div className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Water Filtration Not Configured</div>
                  <p className="text-sm text-gray-500 mb-4">Set up your water filtration system to track filter maintenance and batch counts.</p>
                  <button 
                    onClick={() => onNavigateToSettings?.()}
                    className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 transition"
                  >
                    Configure Water Filtration
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-semibold">Water Filtration</div>
                      <div className="text-xs text-gray-500">
                        Filters: {enabledFilterTypes.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ') || 'None selected'}
                        <span className="mx-2">•</span>
                        Batches since maintenance: <span className="font-semibold">{waterBatchCount}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Status badge */}
                      {(() => {
                        const status = getBreweryMaintenanceStatus(waterRecords, 'Water Filtration', settings);
                        return status.neverDone ? <StatusBadge status='never' label='Never' /> : (status.status === 'overdue' ? <StatusBadge status='overdue' label='Overdue' /> : status.status === 'due-soon' ? <StatusBadge status='due-soon' label='Due' /> : <StatusBadge status='ok' label='OK' />);
                      })()}

                      <button onClick={() => { setMassModalInitialCategory('water'); setMassModalInitialFilters(enabledFilterTypes); setShowMassModal(true); }} className="px-2 py-1 bg-amber-600 text-white rounded text-sm">Record Maintenance</button>
                    </div>
                  </div>

                  {/* Per-filter status grid - only show enabled filters */}
                  {enabledFilterTypes.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      {enabledFilterTypes.map(filterType => {
                        const filterLabel = filterType.charAt(0).toUpperCase() + filterType.slice(1);
                        // Find the most recent record for this specific filter
                        const filterRecords = waterRecords.filter(r => (r.items || []).some(i => i.toLowerCase().includes(filterType)));
                        const lastRecord = filterRecords[0];
                        const monthsSince = lastRecord ? Math.floor((new Date() - new Date(lastRecord.maintenance_date)) / (1000 * 60 * 60 * 24 * 30)) : null;
                        
                        return (
                          <div key={filterType} className="p-2 border rounded text-xs bg-gray-50 dark:bg-gray-700">
                            <div className="font-medium">{filterLabel}</div>
                            <div className="text-gray-500">{lastRecord ? `${monthsSince} mo ago` : 'Never'}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {waterRecords && waterRecords.length > 0 ? (
                    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      {waterRecords.slice(0,3).map(w => (
                        <div key={w.id} className="p-2 border rounded bg-gray-50 dark:bg-gray-700">
                          <div className="text-xs text-gray-500">{new Date(w.maintenance_date).toLocaleString()}</div>
                          <div className="font-medium">{(w.items || []).join(', ')}</div>
                          {w.notes && <div className="text-xs text-gray-500 mt-1">{w.notes}</div>}
                        </div>
                      ))}
                      {waterRecords.length > 3 && <div className="text-xs text-gray-500">Showing recent {Math.min(3, waterRecords.length)} of {waterRecords.length}</div>}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No water filtration records yet.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <LocationMaintenanceModal show={showLocationModal} onClose={() => setShowLocationModal(false)} breweryId={selectedBrewery?.id} locationName={selectedLocationName} maintenanceForm={locationMaintenanceForm} setMaintenanceForm={setLocationMaintenanceForm} onSave={handleSaveLocationMaintenance} selectedLocationTaps={selectedLocationTaps} />
    </div>
  );
};

export default DashboardMaintenanceTab;